import { useState } from 'react';
import { Copy, Plus, Shield, Calendar, AlertCircle, Check } from 'lucide-react';
import { Card, Button, Input, FormLabel, SectionLabel } from '@aaimplatform/ui';
import type { PledgeStatus } from '../../hooks/usePledges';

/**
 * Examiners Page (Sprint 174 D-003 -- partial)
 *
 * FI tenant admin provisions examiner JWTs for regulators (NCUA, OCC,
 * FDIC, state) who need read-only audit access to the FI's pledge data.
 * The MVP path is JWT-claim-only (D-001 + D-002): no Prisma model for
 * examiner accounts, no invite-email flow, no magic-link signup. The
 * tenant admin posts the desired scope, receives a signed JWT, and
 * delivers it to the examiner via secure channel.
 *
 * Past mints are visible via the existing Audit Logs page filtered by
 * `details.action: 'examiner_jwt_mint'` -- not duplicated here so the
 * audit surface stays consolidated.
 *
 * Backend contract: docs/operations/data-portability.md.
 */

interface MintResponse {
  examinerJwt: string;
  examinerUserId: string;
  accessExpiresAt: string;
  jwtExpiresAt: string;
}

interface MintError {
  error?: string;
  message?: string;
  code?: string;
}

/**
 * Every canonical pledge status the examiner scope filter may select, DERIVED
 * from the generated `PledgeStatus` union via an exhaustive keyed record. A
 * status added to schema-registry/platform-admin.openapi.yaml makes the record
 * non-exhaustive and fails tsc, so this list cannot fall behind the enum.
 *
 * Drift history: the prior hand-listed literal offered `ACCEPTED`, which is not
 * a member of the enum (the accept transition writes ACTIVE), and omitted the
 * real ACTIVE. Selecting ACCEPTED minted a scope filter matching no pledge, so
 * the examiner saw an empty evidence set; ACTIVE could not be selected at all.
 *
 * Pinned by src/pages/admin/__tests__/Examiners.test.tsx.
 */
const ALL_PLEDGE_STATUSES: Record<PledgeStatus, true> = {
  PENDING: true,
  ACKNOWLEDGED: true,
  ACTIVE: true,
  RELEASED: true,
  EXPIRED: true,
  WITHDRAWN: true,
  DECLINED: true,
  RETURNED: true,
  // GH #2300 / #2194 node 9: an examiner scoping an evidence request may need
  // the awaiting-member-signature population as readily as any other.
  AWAITING_SIGNATURE: true,
};

export const STATUS_OPTIONS: readonly PledgeStatus[] = Object.keys(
  ALL_PLEDGE_STATUSES,
) as PledgeStatus[];

function defaultExpiry90DaysOut(): string {
  const d = new Date(Date.now() + 90 * 86400 * 1000);
  // type=date input expects YYYY-MM-DD
  return d.toISOString().slice(0, 10);
}

export function ExaminersPage() {
  const [examinerEmail, setExaminerEmail] = useState('');
  const [examinerName, setExaminerName] = useState('');
  const [accessExpiresAt, setAccessExpiresAt] = useState(defaultExpiry90DaysOut());
  const [accessSince, setAccessSince] = useState('');
  const [accessUntil, setAccessUntil] = useState('');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [jwtTtlHours, setJwtTtlHours] = useState(24);

  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [mintResult, setMintResult] = useState<MintResponse | null>(null);
  const [copied, setCopied] = useState(false);

  function toggleStatus(s: string) {
    setStatusFilter((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
    );
  }

  async function handleMint(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setErrorMessage(null);
    setErrorCode(null);
    setMintResult(null);
    setCopied(false);

    try {
      const expiresAtIso = new Date(accessExpiresAt).toISOString();
      const sinceIso = accessSince ? new Date(accessSince).toISOString() : undefined;
      const untilIso = accessUntil ? new Date(accessUntil).toISOString() : undefined;

      const res = await fetch('/api/admin/examiners/mint-jwt', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          examinerEmail,
          examinerName: examinerName || undefined,
          accessExpiresAt: expiresAtIso,
          accessSince: sinceIso,
          accessUntil: untilIso,
          accessStatusFilter: statusFilter.length > 0 ? statusFilter : undefined,
          jwtTtlHours,
        }),
      });

      const body = (await res.json()) as MintResponse | MintError;
      if (!res.ok) {
        const err = body as MintError;
        setErrorMessage(err.message || err.error || 'Mint failed');
        setErrorCode(err.code || `HTTP ${res.status}`);
        return;
      }

      setMintResult(body as MintResponse);
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Mint failed');
      setErrorCode('NETWORK_ERROR');
    } finally {
      setSubmitting(false);
    }
  }

  async function copyJwt() {
    if (!mintResult) return;
    try {
      await navigator.clipboard.writeText(mintResult.examinerJwt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API may be blocked; user can manually select the text
    }
  }

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <div>
          {/* Page title lives in the sticky topnav (AppHeader); the body opens
              with the descriptive subtitle, no redundant H1. */}
          <p className="text-sm font-sans text-tertiary">
            Provision read-only audit credentials for regulators (NCUA, OCC, FDIC, state)
          </p>
        </div>
      </div>

      <Card className="mb-6">
        <div className="flex items-start gap-3 mb-6">
          <Shield className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
          <div>
            <SectionLabel>Mint Examiner JWT</SectionLabel>
            <p className="text-sm font-sans text-tertiary mt-1">
              Generates a signed token that grants the examiner read-only access to your institution's
              pledge data. Deliver the token to the examiner via secure channel (encrypted email,
              password manager, or in-person). Past mints are recorded in the audit trail,
              filtered by action <code>DATA_EXPORT</code> + details.action <code>examiner_jwt_mint</code>.
            </p>
          </div>
        </div>

        <form onSubmit={handleMint} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <FormLabel htmlFor="examinerEmail">Examiner email *</FormLabel>
              <Input
                id="examinerEmail"
                type="email"
                required
                value={examinerEmail}
                onChange={(e) => setExaminerEmail(e.target.value)}
                placeholder="examiner@ncua.gov"
              />
            </div>
            <div>
              <FormLabel htmlFor="examinerName">Examiner name (optional)</FormLabel>
              <Input
                id="examinerName"
                type="text"
                value={examinerName}
                onChange={(e) => setExaminerName(e.target.value)}
                placeholder="NCUA Examiner Jane Doe"
              />
            </div>
          </div>

          <div>
            <FormLabel htmlFor="accessExpiresAt">Access expires at * (max 90 days from now)</FormLabel>
            <Input
              id="accessExpiresAt"
              type="date"
              required
              value={accessExpiresAt}
              onChange={(e) => setAccessExpiresAt(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <FormLabel htmlFor="accessSince">Access since (optional)</FormLabel>
              <Input
                id="accessSince"
                type="date"
                value={accessSince}
                onChange={(e) => setAccessSince(e.target.value)}
              />
              <p className="text-xs font-sans text-tertiary mt-1">
                Lower bound on visible pledge createdAt
              </p>
            </div>
            <div>
              <FormLabel htmlFor="accessUntil">Access until (optional)</FormLabel>
              <Input
                id="accessUntil"
                type="date"
                value={accessUntil}
                onChange={(e) => setAccessUntil(e.target.value)}
              />
              <p className="text-xs font-sans text-tertiary mt-1">
                Upper bound on visible pledge createdAt
              </p>
            </div>
          </div>

          <div>
            <FormLabel>Status filter (optional, empty = all visible)</FormLabel>
            <div className="flex flex-wrap gap-2 mt-2">
              {STATUS_OPTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleStatus(s)}
                  className={`px-3 py-1 rounded-sm text-xs font-sans border transition ${
                    statusFilter.includes(s)
                      ? 'bg-accent text-white border-accent'
                      : 'bg-surface text-tertiary border-recessed hover:border-accent'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div>
            <FormLabel htmlFor="jwtTtlHours">JWT TTL (hours, max 24)</FormLabel>
            <Input
              id="jwtTtlHours"
              type="number"
              min={1}
              max={24}
              value={jwtTtlHours}
              onChange={(e) => setJwtTtlHours(Number(e.target.value))}
            />
            <p className="text-xs font-sans text-tertiary mt-1">
              Token rotation discipline. Examiner offboarding before accessExpiresAt requires
              waiting up to this TTL for the JWT to expire.
            </p>
          </div>

          {errorMessage && (
            <div className="bg-error/5 border border-error/20 rounded-sm p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-error flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-sans font-medium text-error">{errorMessage}</div>
                {errorCode && (
                  <div className="text-xs font-mono text-error/80 mt-1">{errorCode}</div>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <Button type="submit" variant="primary" disabled={submitting}>
              <Plus className="w-4 h-4 mr-2" />
              {submitting ? 'Minting…' : 'Mint Examiner JWT'}
            </Button>
          </div>
        </form>
      </Card>

      {mintResult && (
        <Card>
          <div className="flex items-start gap-3 mb-4">
            <Calendar className="w-5 h-5 text-growth flex-shrink-0 mt-0.5" />
            <div>
              <SectionLabel>Examiner JWT minted</SectionLabel>
              <p className="text-sm font-sans text-tertiary mt-1">
                Deliver the token below to the examiner via secure channel. They paste it into
                their HTTP client as <code>Authorization: Bearer &lt;token&gt;</code>.
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <FormLabel>Examiner user ID</FormLabel>
              <Input readOnly value={mintResult.examinerUserId} />
            </div>
            <div>
              <FormLabel>Access expires at</FormLabel>
              <Input readOnly value={mintResult.accessExpiresAt} />
            </div>
            <div>
              <FormLabel>JWT expires at (token rotation point)</FormLabel>
              <Input readOnly value={mintResult.jwtExpiresAt} />
            </div>
            <div>
              <FormLabel>Examiner JWT</FormLabel>
              <div className="relative">
                <textarea
                  readOnly
                  value={mintResult.examinerJwt}
                  className="w-full h-32 p-3 font-mono text-xs border border-recessed rounded-sm bg-elevated text-primary resize-none"
                />
                <Button
                  type="button"
                  variant="secondary"
                  className="absolute top-2 right-2"
                  onClick={copyJwt}
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4 mr-2" /> Copied
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" /> Copy
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}
    </>
  );
}
